import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { timeAgo, formatNumber, formatDateTime } from "@/lib/format";
import { Switch } from "@/components/ui/switch";
import {
  RobotIcon, LightningIcon, CircleNotchIcon, TrashIcon, RocketLaunchIcon,
  CheckCircleIcon, WarningIcon, ClockIcon, ImageIcon, PlusIcon, MinusIcon, CopyIcon, FilmSlateIcon,
  ArrowsClockwiseIcon, MagnifyingGlassPlusIcon, XIcon, YoutubeLogoIcon, StackIcon, BookmarkSimpleIcon,
  CheckSquareIcon, SquareIcon, SpeakerHighIcon, ForkKnifeIcon, ShuffleIcon,
} from "@phosphor-icons/react";

const localTz = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
};

const STATUS = {
  generating: { c: "#FFB800", i: CircleNotchIcon, label: "Generating", spin: true },
  ready: { c: "#16C784", i: CheckCircleIcon, label: "Ready to publish" },
  published: { c: "#A3E635", i: RocketLaunchIcon, label: "Published" },
  failed: { c: "#FF4D4D", i: WarningIcon, label: "Failed" },
  cancelled: { c: "#8A8A93", i: XIcon, label: "Cancelled" },
  queued: { c: "#60A5FA", i: ClockIcon, label: "Queued" },
};

const VIDEO_SERVICES = [
  { key: "still", label: "Still image · narration only (cheapest)" },
  { key: "flashcards", label: "Magic Flash Cards · AI images + narration (no video service)" },
  { key: "impossible", label: "Impossible Upgrades · AI transformation (no video service)" },
  { key: "recipe", label: "Recipe Fix · cooking problem→solution (no video service)" },
  { key: "atlas_pro", label: "AtlasCloud · Seedance 1.5 Pro" },
  { key: "atlas_fast", label: "AtlasCloud · Seedance 1.5 Pro Fast" },
  { key: "runway_25", label: "Runway · Seedance 2.5" },
  { key: "runway_20", label: "Runway · Seedance 2.0" },
];
const LANGUAGES = [
  { key: "en", label: "English" },
  { key: "es", label: "Spanish" },
];
const fmtElapsed = (iso) => {
  if (!iso) return "0:00";
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  const m = Math.floor(secs / 60), s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const STEP_ORDER = [
  ["idea", "Idea"], ["title", "Title"], ["description", "Desc"],
  ["image", "Image"], ["voice", "Voice"], ["video", "Video"], ["review", "Review"], ["upload", "Upload"],
];
const StepStrip = ({ steps }) => {
  if (!steps || typeof steps !== "object") return null;
  const items = STEP_ORDER.filter(([k]) => steps[k] !== undefined);
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2" data-testid="fsp-steps">
      {items.map(([k, label]) => {
        const s = steps[k];
        let color = "#9c9ca8", icon;
        if (s === "done") { color = "#16C784"; icon = <CheckCircleIcon size={12} weight="fill" />; }
        else if (s === "running") { color = "#FFB800"; icon = <CircleNotchIcon size={12} className="spin-slow" />; }
        else if (s === "failed") { color = "#FF4D4D"; icon = <WarningIcon size={12} weight="fill" />; }
        else if (s === "skipped") { icon = <span className="w-2 h-2 rounded-full bg-[#2a2a30] inline-block" />; }
        else { icon = <span className="w-2 h-2 rounded-full border border-[#3a3a40] inline-block" />; }
        return (
          <span key={k} data-testid={`fsp-step-${k}-${s}`}
            className={`flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider ${s === "skipped" ? "line-through opacity-60" : ""}`}
            style={{ color }}>
            {icon} {label}
          </span>
        );
      })}
    </div>
  );
};

const FSPPanel = ({ channelId }) => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genService, setGenService] = useState("");
  const [genCharacter, setGenCharacter] = useState("none");
  const [genBackground, setGenBackground] = useState("");
  const [, setNowTick] = useState(0);
  const [jobs, setJobs] = useState([]);
  const [preview, setPreview] = useState(null);
  const [yt, setYt] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [tab, setTab] = useState("current");
  const [selected, setSelected] = useState(() => new Set());
  const [lfTopic, setLfTopic] = useState("");
  const [lfBusy, setLfBusy] = useState(false);
  const [voices, setVoices] = useState([]);
  const [voiceId, setVoiceId] = useState("");
  const [rcTopic, setRcTopic] = useState("");
  const [rcCands, setRcCands] = useState([]);
  const [rcSel, setRcSel] = useState("");
  const [rcSearching, setRcSearching] = useState(false);
  const [rcBusy, setRcBusy] = useState(false);
  const [doneTimes, setDoneTimes] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [charUploading, setCharUploading] = useState(false);
  const [backgrounds, setBackgrounds] = useState([]);
  const [bgUploading, setBgUploading] = useState(false);
  const [sampling, setSampling] = useState(false);
  const pollRef = useRef(null);
  const charInputRef = useRef(null);
  const bgInputRef = useRef(null);

  const loadYt = useCallback(async () => {
    try {
      const res = await api.get(`/youtube/oauth/status?channel_id=${channelId}`);
      setYt(res.data);
    } catch { setYt({ configured: false, connected: false }); }
  }, [channelId]);

  const connectYt = async () => {
    if (connecting) return;
    setConnecting(true);
    try {
      const res = await api.get(`/youtube/oauth/start?channel_id=${channelId}`);
      if (res.data.authorization_url) {
        window.location.href = res.data.authorization_url;
      } else {
        toast.error("Could not start YouTube connect");
        setConnecting(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start YouTube connect");
      setConnecting(false);
    }
  };

  const disconnectYt = async () => {
    try { await api.post(`/youtube/oauth/disconnect?channel_id=${channelId}`); toast.success("Disconnected YouTube"); loadYt(); }
    catch { toast.error("Could not disconnect"); }
  };

  const loadSettings = useCallback(async () => {
    const res = await api.get(`/fsp/settings?channel_id=${channelId}`);
    const s = res.data;
    if (!s.timezone || s.timezone === "UTC") s.timezone = localTz();
    if (!s.post_times || s.post_times.length === 0) s.post_times = ["09:00"];
    if (!s.posts_per_day) s.posts_per_day = s.post_times.length || 1;
    if (typeof s.random_times !== "boolean") s.random_times = false;
    if (!s.random_start) s.random_start = "08:00";
    if (!s.random_end) s.random_end = "20:00";
    if (typeof s.shorts_native_audio !== "boolean") s.shorts_native_audio = true;
    if (!s.video_service) s.video_service = "atlas_pro";
    if (!s.language) s.language = "en";
    if (s.character_id === undefined) s.character_id = null;
    if (s.background_id === undefined) s.background_id = null;
    if (s.narrator_voice_id === undefined) s.narrator_voice_id = null;
    if (typeof s.lipsync !== "boolean") s.lipsync = false;
    if (typeof s.made_for_kids !== "boolean") s.made_for_kids = false;
    if (typeof s.content_focus !== "string") s.content_focus = "";
    if (typeof s.recipe_long_form !== "boolean") s.recipe_long_form = false;
    setSettings(s);
    // Mirror the saved default into the "Generate 1 now" character selector so it survives refresh.
    setGenCharacter(s.character_id === "random" ? "random" : (s.character_id || "none"));
  }, [channelId]);

  const loadQueue = useCallback(async () => {
    const res = await api.get(`/fsp/queue?channel_id=${channelId}`);
    setJobs(res.data.jobs || []);
  }, [channelId]);

  const loadScheduleStatus = useCallback(async () => {
    try {
      const res = await api.get(`/fsp/schedule-status?channel_id=${channelId}`);
      setDoneTimes(res.data.done_times || []);
    } catch { setDoneTimes([]); }
  }, [channelId]);

  const loadCharacters = useCallback(async () => {
    try {
      const res = await api.get(`/fsp/characters?channel_id=${channelId}`);
      setCharacters(res.data.characters || []);
    } catch { setCharacters([]); }
  }, [channelId]);

  const uploadCharacter = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    const name = window.prompt("Name this character (e.g. 'Sofia', 'Host'):", file.name.replace(/\.[^.]+$/, ""));
    if (name === null) return;
    setCharUploading(true);
    try {
      const fd = new FormData();
      fd.append("channel_id", channelId);
      fd.append("name", name || "Character");
      fd.append("file", file);
      await api.post("/fsp/characters", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Character added");
      loadCharacters();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add character");
    } finally {
      setCharUploading(false);
    }
  };

  const previewVoiceSample = async () => {
    setSampling(true);
    try {
      const res = await api.post("/fsp/voices/preview",
        { voice_id: settings?.narrator_voice_id || null, language: settings?.language || "en" },
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audio.onended = () => URL.revokeObjectURL(url);
      await audio.play();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not play sample");
    } finally {
      setSampling(false);
    }
  };

  const deleteCharacter = async (id) => {
    try {
      await api.delete(`/fsp/characters/${id}`);
      if (settings?.character_id === id) setSettings((s) => ({ ...s, character_id: null }));
      loadCharacters();
      toast.success("Character removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove");
    }
  };

  const loadBackgrounds = useCallback(async () => {
    try {
      const res = await api.get(`/fsp/backgrounds?channel_id=${channelId}`);
      setBackgrounds(res.data.backgrounds || []);
    } catch { setBackgrounds([]); }
  }, [channelId]);

  const uploadBackground = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    const name = window.prompt("Name this background (e.g. 'Cozy Kitchen', 'Sunset'):", file.name.replace(/\.[^.]+$/, ""));
    if (name === null) return;
    setBgUploading(true);
    try {
      const fd = new FormData();
      fd.append("channel_id", channelId);
      fd.append("name", name || "Background");
      fd.append("file", file);
      await api.post("/fsp/backgrounds", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Background added");
      loadBackgrounds();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add background");
    } finally {
      setBgUploading(false);
    }
  };

  const deleteBackground = async (id) => {
    try {
      await api.delete(`/fsp/backgrounds/${id}`);
      if (settings?.background_id === id) setSettings((s) => ({ ...s, background_id: null }));
      loadBackgrounds();
      toast.success("Background removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove");
    }
  };

  useEffect(() => { loadSettings(); loadQueue(); loadScheduleStatus(); loadCharacters(); loadBackgrounds(); }, [loadSettings, loadQueue, loadScheduleStatus, loadCharacters, loadBackgrounds]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/fsp/voices");
        setVoices(res.data.voices || []);
        setVoiceId((v) => v || res.data.default || "");
      } catch { /* voices optional */ }
    })();
  }, []);

  // Clear multi-select when switching folders.
  useEffect(() => { setSelected(new Set()); }, [tab]);

  // YouTube connect status + handle the OAuth return (?yt=connected|error).
  useEffect(() => {
    loadYt();
    const params = new URLSearchParams(window.location.search);
    const ytParam = params.get("yt");
    if (ytParam === "connected") {
      toast.success("YouTube connected for uploads");
      params.delete("yt");
      window.history.replaceState({}, "", `${window.location.pathname}${params.toString() ? "?" + params : ""}`);
      loadYt();
    } else if (ytParam === "error") {
      const reason = params.get("reason");
      const map = {
        access_denied: "You cancelled or denied access. Try again and click Allow.",
        redirect_uri_mismatch: "Redirect URL mismatch — the app's YouTube OAuth redirect URI needs to include this domain (contact support).",
        state_expired: "The connect link expired. Click Connect YouTube again.",
        missing_code: "Google didn't return an authorization code. Try again.",
        invalid_grant: "Authorization expired. Click Connect YouTube and try again.",
      };
      toast.error(map[reason] || `YouTube connection failed${reason ? ` (${reason})` : ""} — try again`);
      params.delete("yt");
      params.delete("reason");
      window.history.replaceState({}, "", `${window.location.pathname}${params.toString() ? "?" + params : ""}`);
    }
  }, [loadYt]);

  // Poll while anything is generating.
  useEffect(() => {
    const active = jobs.some((j) => j.status === "generating" || j.status === "queued" || j.video_generating || j.publishing);
    clearInterval(pollRef.current);
    if (active) pollRef.current = setInterval(() => { loadQueue(); loadScheduleStatus(); }, 4000);
    return () => clearInterval(pollRef.current);
  }, [jobs, loadQueue, loadScheduleStatus]);

  // 1s ticker to drive the live "elapsed" timer on generating jobs.
  useEffect(() => {
    const active = jobs.some((j) => j.status === "generating");
    if (!active) return;
    const t = setInterval(() => setNowTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [jobs]);

  const cancelJob = async (id) => {
    try {
      const res = await api.post(`/fsp/jobs/${id}/cancel`);
      toast.success(res.data?.message || "Cancelling…");
      loadQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    }
  };

  const setPostsPerDay = (n) => {
    n = Math.max(1, Math.min(12, n));
    setSettings((s) => {
      const times = [...(s.post_times || [])];
      while (times.length < n) times.push("12:00");
      times.length = n;
      return { ...s, posts_per_day: n, post_times: times };
    });
  };

  const setTime = (i, val) =>
    setSettings((s) => {
      const times = [...s.post_times];
      times[i] = val;
      return { ...s, post_times: times };
    });

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        enabled: settings.enabled,
        posts_per_day: settings.posts_per_day,
        post_times: settings.post_times,
        timezone: settings.timezone,
        made_for_kids: settings.made_for_kids,
        random_times: settings.random_times,
        random_start: settings.random_start,
        random_end: settings.random_end,
        shorts_native_audio: settings.shorts_native_audio,
        video_service: settings.video_service,
        language: settings.language,
        content_focus: settings.content_focus ?? "",
        recipe_long_form: settings.recipe_long_form ?? false,
        character_id: settings.character_id ?? null,
        background_id: settings.background_id ?? null,
        narrator_voice_id: settings.narrator_voice_id ?? null,
        lipsync: settings.lipsync,
      };
      await api.put(`/fsp/settings?channel_id=${channelId}`, body);
      toast.success(settings.enabled ? "FSP autopilot is ON" : "FSP settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const generateNow = async () => {
    setGenerating(true);
    try {
      const svc = genService || settings?.video_service || "";
      const params = [`channel_id=${channelId}`];
      if (svc) params.push(`service=${svc}`);
      if (genCharacter) params.push(`character_id=${encodeURIComponent(genCharacter)}`);
      if (genBackground) params.push(`background_id=${encodeURIComponent(genBackground)}`);
      await api.post(`/fsp/generate?${params.join("&")}`);
      toast.success("Generating a new post — it'll appear in the queue shortly");
      // Poll several times: the job may take a few seconds to insert, and the interval poller only runs once
      // a generating job is already visible — so a single delayed poll could miss it and it'd never show.
      [800, 2000, 4000, 7000, 11000].forEach((ms) => setTimeout(loadQueue, ms));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start generation");
    } finally {
      setGenerating(false);
    }
  };

  const generateLongform = async () => {
    if (!lfTopic.trim()) { toast.error("Enter a topic first"); return; }
    setLfBusy(true);
    try {
      const res = await api.post(`/fsp/longform/generate?channel_id=${channelId}`, { topic: lfTopic.trim(), voice_id: voiceId || null });
      toast.success(res.data.message || "Long-form render started");
      setLfTopic("");
      setTimeout(loadQueue, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start long-form render");
    } finally {
      setLfBusy(false);
    }
  };

  const searchRecipe = async () => {
    if (!rcTopic.trim()) { toast.error("Enter a meal to search"); return; }
    setRcSearching(true); setRcCands([]); setRcSel("");
    try {
      const res = await api.get(`/fsp/recipe/candidates?channel_id=${channelId}&topic=${encodeURIComponent(rcTopic.trim())}`);
      const c = res.data.candidates || [];
      setRcCands(c);
      if (c[0]) setRcSel(c[0].video_id);
      if (!c.length) toast.error("No recipe videos found — try another meal");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Search failed");
    } finally { setRcSearching(false); }
  };

  const generateRecipe = async () => {
    if (!rcSel) { toast.error("Pick a source video first"); return; }
    setRcBusy(true);
    try {
      const res = await api.post(`/fsp/recipe/generate?channel_id=${channelId}`, { video_id: rcSel, topic: rcTopic.trim(), voice_id: voiceId || null });
      toast.success(res.data.message || "Recipe render started");
      setRcCands([]); setRcSel(""); setRcTopic("");
      setTimeout(loadQueue, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start recipe render");
    } finally { setRcBusy(false); }
  };

  const publish = async (id) => {
    try {
      const res = await api.post(`/fsp/jobs/${id}/publish`);
      if (res.data.publishing) {
        toast.success("Publishing to YouTube… it'll move to Published in a moment.");
      } else if (res.data.youtube_url) {
        toast.success("Uploaded to YouTube as Private — flip to Public in YouTube Studio", {
          action: { label: "Open", onClick: () => window.open(res.data.youtube_url, "_blank") },
        });
      } else {
        toast.success(res.data.note || "Published");
      }
      loadQueue();
    } catch (e) {
      if (e.response?.status === 428) {
        toast.error("Connect your YouTube account first (button at the top of FSP).");
      } else {
        toast.error(e.response?.data?.detail || "Could not publish");
      }
    }
  };

  const del = async (id) => {
    try { await api.delete(`/fsp/jobs/${id}`); loadQueue(); } catch { toast.error("Could not delete"); }
  };

  const setBucket = async (id, bucket) => {
    try {
      await api.post(`/fsp/jobs/${id}/bucket`, { bucket });
      toast.success(bucket === "later" ? "Moved to Later" : "Moved to Current");
      loadQueue();
    } catch { toast.error("Could not move"); }
  };

  const markPublished = async (id) => {
    try {
      await api.post(`/fsp/jobs/${id}/mark-published`);
      toast.success("Moved to Published");
      loadQueue();
    } catch { toast.error("Could not move to Published"); }
  };

  const unpublish = async (id) => {
    try {
      await api.post(`/fsp/jobs/${id}/unpublish`);
      toast.success("Moved back to Current (still live on YouTube if it was uploaded)");
      loadQueue();
    } catch { toast.error("Could not move back to Current"); }
  };

  const toggleSel = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const clearSel = () => setSelected(new Set());

  const bulkMove = async (bucket) => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      await api.post("/fsp/jobs/bulk-bucket", { ids, bucket });
      toast.success(`Moved ${ids.length} to ${bucket === "later" ? "Later" : "Current"}`);
      clearSel(); loadQueue();
    } catch { toast.error("Could not move selected"); }
  };

  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      const r = await api.post("/fsp/jobs/bulk-delete", { ids });
      toast.success(`Deleted ${r.data.deleted} post${r.data.deleted === 1 ? "" : "s"}`);
      clearSel(); loadQueue();
    } catch { toast.error("Could not delete selected"); }
  };

  const regenerate = async (id) => {
    try {
      await api.post(`/fsp/jobs/${id}/regenerate`);
      toast.success("Regenerating with spellcheck — the new version will appear shortly");
      setTimeout(loadQueue, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not regenerate");
    }
  };

  const retryVideo = async (id) => {
    try {
      await api.post(`/fsp/jobs/${id}/retry-video`);
      toast.success("Retrying the Seedance 1.5 Pro video on your existing cover — it'll appear shortly");
      setTimeout(loadQueue, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not retry video");
    }
  };

  const copyText = (t, label) => {
    navigator.clipboard.writeText(t || "");
    toast.success(`${label} copied`);
  };

  if (!settings) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="fsp-loading">
        <CircleNotchIcon size={32} className="text-[#16C784] spin-slow" />
      </div>
    );
  }

  return (
    <motion.div data-testid="fsp-panel" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-full border border-[#16C784]/50 flex items-center justify-center">
          <RobotIcon size={20} className="text-[#16C784]" weight="fill" />
        </div>
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#A3E635]">// Full Self Posting</div>
          <h2 className="font-rajdhani font-bold text-xl tracking-wide">FSP AUTOPILOT</h2>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,380px)_1fr] gap-4">
        {/* Settings */}
        <div className="bg-[#0C0C0E] border border-[#222226] p-5 h-fit" data-testid="fsp-settings">
          <div className="flex items-center justify-between border-b border-[#1a1a1e] pb-3 mb-4">
            <div>
              <div className="font-rajdhani font-bold text-base text-white">Autopilot</div>
              <div className="font-mono text-[10px] text-[#9c9ca8]">Auto-generate posts on a schedule</div>
            </div>
            <Switch
              data-testid="fsp-enable-toggle"
              checked={settings.enabled}
              onCheckedChange={(v) => setSettings((s) => ({ ...s, enabled: v }))}
              className="data-[state=checked]:bg-[#16C784]"
            />
          </div>

          <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#9c9ca8]">Posts per day</label>
          <div className="flex items-center gap-2 mt-1.5 mb-4">
            <button data-testid="fsp-ppd-minus" onClick={() => setPostsPerDay(settings.posts_per_day - 1)}
              className="w-8 h-8 border border-[#222226] text-[#A0A0AB] hover:border-[#16C784]/50 hover:text-[#16C784] flex items-center justify-center transition-colors">
              <MinusIcon size={14} />
            </button>
            <div data-testid="fsp-ppd-value" className="font-rajdhani font-bold text-2xl text-white w-10 text-center">{settings.posts_per_day}</div>
            <button data-testid="fsp-ppd-plus" onClick={() => setPostsPerDay(settings.posts_per_day + 1)}
              className="w-8 h-8 border border-[#222226] text-[#A0A0AB] hover:border-[#16C784]/50 hover:text-[#16C784] flex items-center justify-center transition-colors">
              <PlusIcon size={14} />
            </button>
            <span className="font-mono text-[10px] text-[#9c9ca8] ml-1">/ day</span>
          </div>

          <div className="flex items-center justify-between mb-1.5">
            <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#9c9ca8]">Randomize times</label>
            <Switch
              data-testid="fsp-random-toggle"
              checked={settings.random_times}
              onCheckedChange={(v) => setSettings((s) => ({ ...s, random_times: v }))}
              className="data-[state=checked]:bg-[#16C784] scale-90"
            />
          </div>

          <div className="flex items-center justify-between mb-1">
            <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#9c9ca8]">Made for kids</label>
            <Switch
              data-testid="fsp-made-for-kids-toggle"
              checked={settings.made_for_kids}
              onCheckedChange={(v) => setSettings((s) => ({ ...s, made_for_kids: v }))}
              className="data-[state=checked]:bg-[#16C784] scale-90"
            />
          </div>
          <p className="font-mono text-[9px] text-[#9c9ca8] leading-relaxed mb-4">
            {settings.made_for_kids
              ? "Uploads are set as MADE FOR KIDS on YouTube (comments off, no personalized ads)."
              : "Uploads are set as NOT made for kids (default)."}
          </p>

          <div className="flex items-center justify-between mb-1">
            <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#9c9ca8]">Recipe: Long-form (16:9)</label>
            <Switch
              data-testid="fsp-recipe-longform-toggle"
              checked={settings.recipe_long_form}
              onCheckedChange={(v) => setSettings((s) => ({ ...s, recipe_long_form: v }))}
              className="data-[state=checked]:bg-[#16C784] scale-90"
            />
          </div>
          <p className="font-mono text-[9px] text-[#9c9ca8] leading-relaxed mb-4">
            {settings.recipe_long_form
              ? "Recipe videos render 16:9 landscape — posted as a normal video (NOT a Short). Custom ❌/✅ thumbnails apply reliably here."
              : "Recipe videos render 9:16 vertical Shorts (default). YouTube controls the Short cover, so the ❌/✅ card can't be forced."}
          </p>

          {settings.random_times ? (
            <div data-testid="fsp-random-window" className="mb-4">
              <p className="font-mono text-[9px] text-[#9c9ca8] leading-relaxed mb-2">
                FSP picks {settings.posts_per_day} random {settings.posts_per_day > 1 ? "times" : "time"} each day inside the window below ({settings.timezone}). A fresh set of times is chosen daily.
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="font-mono text-[9px] uppercase tracking-wider text-[#9c9ca8]">From</span>
                  <input
                    data-testid="fsp-random-start"
                    type="time"
                    value={settings.random_start}
                    onChange={(e) => setSettings((s) => ({ ...s, random_start: e.target.value }))}
                    className="w-full mt-1 bg-[#050505] border border-[#222226] focus:border-[#16C784] text-white font-mono text-sm px-2.5 py-2 outline-none transition-colors"
                  />
                </div>
                <div>
                  <span className="font-mono text-[9px] uppercase tracking-wider text-[#9c9ca8]">To</span>
                  <input
                    data-testid="fsp-random-end"
                    type="time"
                    value={settings.random_end}
                    onChange={(e) => setSettings((s) => ({ ...s, random_end: e.target.value }))}
                    className="w-full mt-1 bg-[#050505] border border-[#222226] focus:border-[#16C784] text-white font-mono text-sm px-2.5 py-2 outline-none transition-colors"
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#9c9ca8]">Post times ({settings.timezone})</label>
              <div className="grid grid-cols-2 gap-2 mt-1.5 mb-4">
                {settings.post_times.map((t, i) => {
                  const done = doneTimes.includes(t);
                  return (
                    <div key={i} className="relative">
                      <input
                        data-testid={`fsp-time-${i}`}
                        type="time"
                        value={t}
                        onChange={(e) => setTime(i, e.target.value)}
                        title={done ? "Posted today" : undefined}
                        className={`w-full font-mono text-sm px-2.5 py-2 outline-none transition-colors ${
                          done
                            ? "bg-[#16C784]/10 border border-[#16C784] text-[#16C784] pr-8"
                            : "bg-[#050505] border border-[#222226] focus:border-[#16C784] text-white"
                        }`}
                      />
                      {done && (
                        <CheckCircleIcon
                          size={16} weight="fill"
                          data-testid={`fsp-time-done-${i}`}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-[#16C784] pointer-events-none"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <div className="bg-[#050505] border border-[#222226] px-3 py-2.5 mb-4">
            <label className="font-mono text-[11px] text-[#A0A0AB] block mb-1.5">Video creation service</label>
            <select
              data-testid="fsp-video-service-select"
              value={settings.video_service || "atlas_pro"}
              onChange={(e) => setSettings((s) => ({ ...s, video_service: e.target.value }))}
              className="w-full bg-[#0C0C0E] border border-[#222226] text-white font-rajdhani font-bold text-sm px-2 py-2 focus:outline-none focus:border-[#16C784]/60"
            >
              {VIDEO_SERVICES.map((v) => (
                <option key={v.key} value={v.key}>{v.label}</option>
              ))}
            </select>
            <span className="font-mono text-[9px] text-[#9c9ca8] mt-1 block">Used for autopilot &amp; manual generations on this channel</span>
          </div>

          <div className="bg-[#050505] border border-[#222226] px-3 py-2.5 mb-4">
            <label className="font-mono text-[11px] text-[#A0A0AB] block mb-1.5">Content language</label>
            <select
              data-testid="fsp-language-select"
              value={settings.language || "en"}
              onChange={(e) => setSettings((s) => ({ ...s, language: e.target.value }))}
              className="w-full bg-[#0C0C0E] border border-[#222226] text-white font-rajdhani font-bold text-sm px-2 py-2 focus:outline-none focus:border-[#16C784]/60"
            >
              {LANGUAGES.map((l) => (
                <option key={l.key} value={l.key}>{l.label}</option>
              ))}
            </select>
            <span className="font-mono text-[9px] text-[#9c9ca8] mt-1 block">Titles, descriptions, on-image text &amp; narration are written in this language</span>
          </div>

          <div className="bg-[#050505] border border-[#222226] px-3 py-2.5 mb-4">
            <label className="font-mono text-[11px] text-[#A0A0AB] block mb-1.5">Content focus <span className="text-[#9c9ca8]">(optional)</span></label>
            <input
              data-testid="fsp-content-focus-input"
              type="text"
              value={settings.content_focus || ""}
              onChange={(e) => setSettings((s) => ({ ...s, content_focus: e.target.value }))}
              placeholder="e.g. impossible viral desserts"
              className="w-full bg-[#0C0C0E] border border-[#222226] text-white font-rajdhani font-bold text-sm px-2 py-2 focus:outline-none focus:border-[#16C784]/60 placeholder:text-[#9c9ca8] placeholder:font-normal"
            />
            <span className="font-mono text-[9px] text-[#9c9ca8] mt-1 block">
              {settings.content_focus
                ? `Autopilot ideas are locked to "${settings.content_focus}" — the auto-detected niche is ignored`
                : "Leave blank to auto-detect the niche from your uploads. Set a theme to steer every idea (e.g. desserts, pasta)"}
            </span>
          </div>


          <div className="bg-[#050505] border border-[#222226] px-3 py-2.5 mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="font-mono text-[11px] text-[#A0A0AB]">Character</label>
              <input ref={charInputRef} type="file" accept="image/*" className="hidden"
                data-testid="fsp-character-file" onChange={uploadCharacter} />
              <button data-testid="fsp-character-add" onClick={() => charInputRef.current?.click()} disabled={charUploading}
                className="flex items-center gap-1 border border-[#16C784]/40 text-[#16C784] hover:bg-[#16C784]/10 disabled:opacity-50 font-rajdhani font-bold uppercase tracking-wider text-[10px] px-2 py-1 transition-colors">
                {charUploading ? <CircleNotchIcon size={12} className="spin-slow" /> : <PlusIcon size={12} weight="bold" />} Add photo
              </button>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <button data-testid="fsp-character-none" onClick={() => setSettings((s) => ({ ...s, character_id: null }))}
                className={`aspect-square flex flex-col items-center justify-center gap-1 border text-[9px] font-mono transition-colors ${!settings.character_id ? "border-[#16C784] text-[#16C784] bg-[#16C784]/5" : "border-[#222226] text-[#9c9ca8] hover:border-[#333]"}`}>
                <ImageIcon size={16} /> None
              </button>
              <button data-testid="fsp-character-random" onClick={() => setSettings((s) => ({ ...s, character_id: "random" }))}
                className={`aspect-square flex flex-col items-center justify-center gap-1 border text-[9px] font-mono transition-colors ${settings.character_id === "random" ? "border-[#16C784] text-[#16C784] bg-[#16C784]/5" : "border-[#222226] text-[#9c9ca8] hover:border-[#333]"}`}>
                <ShuffleIcon size={16} /> Random
              </button>
              {characters.map((ch) => (
                <div key={ch.id} className="relative group">
                  <button data-testid={`fsp-character-${ch.id}`} onClick={() => setSettings((s) => ({ ...s, character_id: ch.id }))}
                    title={ch.name}
                    className={`w-full aspect-square overflow-hidden border transition-colors ${settings.character_id === ch.id ? "border-[#16C784] ring-1 ring-[#16C784]" : "border-[#222226] hover:border-[#333]"}`}>
                    <img src={`${API}/fsp/characters/${ch.id}/image`} alt={ch.name} className="w-full h-full object-cover" />
                  </button>
                  <button data-testid={`fsp-character-del-${ch.id}`} onClick={() => deleteCharacter(ch.id)}
                    className="absolute -top-1.5 -right-1.5 bg-[#FF4D4D] text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <TrashIcon size={10} weight="bold" />
                  </button>
                  <span className="block truncate text-center font-mono text-[8px] text-[#8A8A93] mt-0.5">{ch.name}</span>
                </div>
              ))}
            </div>
            <span className="font-mono text-[9px] text-[#9c9ca8] mt-2 block">None = no people on the cover. Random = pick a random saved character each time. Or pick a specific person below.</span>
          </div>

          <div className="bg-[#050505] border border-[#222226] px-3 py-2.5 mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="font-mono text-[11px] text-[#A0A0AB]">Background</label>
              <input ref={bgInputRef} type="file" accept="image/*" className="hidden"
                data-testid="fsp-background-file" onChange={uploadBackground} />
              <button data-testid="fsp-background-add" onClick={() => bgInputRef.current?.click()} disabled={bgUploading}
                className="flex items-center gap-1 border border-[#16C784]/40 text-[#16C784] hover:bg-[#16C784]/10 disabled:opacity-50 font-rajdhani font-bold uppercase tracking-wider text-[10px] px-2 py-1 transition-colors">
                {bgUploading ? <CircleNotchIcon size={12} className="spin-slow" /> : <PlusIcon size={12} weight="bold" />} Add image
              </button>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <button data-testid="fsp-background-none" onClick={() => setSettings((s) => ({ ...s, background_id: null }))}
                className={`aspect-square flex flex-col items-center justify-center gap-1 border text-[9px] font-mono transition-colors ${!settings.background_id ? "border-[#16C784] text-[#16C784] bg-[#16C784]/5" : "border-[#222226] text-[#9c9ca8] hover:border-[#333]"}`}>
                <ImageIcon size={16} /> None
              </button>
              {backgrounds.map((bg) => (
                <div key={bg.id} className="relative group">
                  <button data-testid={`fsp-background-${bg.id}`} onClick={() => setSettings((s) => ({ ...s, background_id: bg.id }))}
                    title={bg.name}
                    className={`w-full aspect-square overflow-hidden border transition-colors ${settings.background_id === bg.id ? "border-[#16C784] ring-1 ring-[#16C784]" : "border-[#222226] hover:border-[#333]"}`}>
                    <img src={`${API}/fsp/backgrounds/${bg.id}/image`} alt={bg.name} className="w-full h-full object-cover" />
                  </button>
                  <button data-testid={`fsp-background-del-${bg.id}`} onClick={() => deleteBackground(bg.id)}
                    className="absolute -top-1.5 -right-1.5 bg-[#FF4D4D] text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <TrashIcon size={10} weight="bold" />
                  </button>
                  <span className="block truncate text-center font-mono text-[8px] text-[#8A8A93] mt-0.5">{bg.name}</span>
                </div>
              ))}
            </div>
            <span className="font-mono text-[9px] text-[#9c9ca8] mt-2 block">Upload a setting/backdrop and it's used as the scene behind the subject on each cover</span>
          </div>

          <div className="flex items-center justify-between bg-[#050505] border border-[#222226] px-3 py-2 mb-4">
            <div className="flex flex-col">
              <span className="font-mono text-[11px] text-[#A0A0AB]">Voiceover on Shorts</span>
              <span className="font-mono text-[9px] text-[#9c9ca8] mt-0.5">An AI voice reads the on-screen title text aloud on each Short</span>
            </div>
            <Switch
              data-testid="fsp-native-audio-toggle"
              checked={settings.shorts_native_audio}
              onCheckedChange={(v) => setSettings((s) => ({ ...s, shorts_native_audio: v }))}
              className="data-[state=checked]:bg-[#16C784] scale-90"
            />
          </div>

          {settings.shorts_native_audio && (
            <>
              <div className="bg-[#050505] border border-[#222226] px-3 py-2.5 mb-4">
                <label className="font-mono text-[11px] text-[#A0A0AB] block mb-1.5">Shorts narrator voice</label>
                <div className="flex items-center gap-2">
                  <select
                    data-testid="fsp-narrator-voice-select"
                    value={settings.narrator_voice_id || ""}
                    onChange={(e) => setSettings((s) => ({ ...s, narrator_voice_id: e.target.value || null }))}
                    className="flex-1 bg-[#0C0C0E] border border-[#222226] text-white font-rajdhani font-bold text-sm px-2 py-2 focus:outline-none focus:border-[#16C784]/60"
                  >
                    <option value="">Default narrator</option>
                    {voices.map((v) => (
                      <option key={v.voice_id} value={v.voice_id}>{v.name}</option>
                    ))}
                  </select>
                  <button type="button" data-testid="fsp-narrator-sample"
                    onClick={previewVoiceSample} disabled={sampling}
                    title="Hear this voice read a sample Short line"
                    className="flex items-center gap-1 border border-[#16C784]/40 text-[#16C784] hover:bg-[#16C784]/10 disabled:opacity-50 font-rajdhani font-bold uppercase tracking-wider text-[10px] px-2 py-2 transition-colors shrink-0">
                    {sampling ? <CircleNotchIcon size={13} className="spin-slow" /> : <SpeakerHighIcon size={13} />} Hear sample
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between bg-[#050505] border border-[#222226] px-3 py-2 mb-4">
                <div className="flex flex-col pr-2">
                  <span className="font-mono text-[11px] text-[#A0A0AB]">Lip-sync (mouth moves)</span>
                  <span className="font-mono text-[9px] text-[#9c9ca8] mt-0.5">Character's lips move to the voice (slower, costs more)</span>
                </div>
                <Switch
                  data-testid="fsp-lipsync-toggle"
                  checked={settings.lipsync}
                  onCheckedChange={(v) => setSettings((s) => ({ ...s, lipsync: v }))}
                  className="data-[state=checked]:bg-[#16C784] scale-90"
                />
              </div>
            </>
          )}

          <div className="flex items-center justify-between bg-[#050505] border border-[#222226] px-3 py-2 mb-4">
            <span className="font-mono text-[11px] text-[#A0A0AB]">Audience</span>
            <span className="font-rajdhani font-bold text-sm text-[#16C784]">Not made for kids</span>
          </div>

          <div className="flex items-center gap-2">
            <span className="font-mono text-[9px] text-[#9c9ca8] uppercase tracking-wider shrink-0">Generate with</span>
            <select
              data-testid="fsp-generate-service-select"
              value={genService || settings.video_service || "atlas_pro"}
              onChange={(e) => setGenService(e.target.value)}
              className="flex-1 bg-[#0C0C0E] border border-[#222226] text-[#A0A0AB] font-rajdhani font-bold text-xs px-2 py-1.5 focus:outline-none focus:border-[#16C784]/60"
            >
              {VIDEO_SERVICES.map((v) => (
                <option key={v.key} value={v.key}>{v.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="font-mono text-[9px] text-[#9c9ca8] uppercase tracking-wider shrink-0">Character</span>
            <select
              data-testid="fsp-generate-character-select"
              value={genCharacter}
              onChange={(e) => setGenCharacter(e.target.value)}
              className="flex-1 bg-[#0C0C0E] border border-[#222226] text-[#A0A0AB] font-rajdhani font-bold text-xs px-2 py-1.5 focus:outline-none focus:border-[#16C784]/60"
            >
              <option value="none">None</option>
              <option value="random">Random</option>
              {characters.map((ch) => (
                <option key={ch.id} value={ch.id}>{ch.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="font-mono text-[9px] text-[#9c9ca8] uppercase tracking-wider shrink-0">Background</span>
            <select
              data-testid="fsp-generate-background-select"
              value={genBackground}
              onChange={(e) => setGenBackground(e.target.value)}
              className="flex-1 bg-[#0C0C0E] border border-[#222226] text-[#A0A0AB] font-rajdhani font-bold text-xs px-2 py-1.5 focus:outline-none focus:border-[#16C784]/60"
            >
              <option value="">Channel default</option>
              <option value="none">None (AI-generated)</option>
              {backgrounds.map((bg) => (
                <option key={bg.id} value={bg.id}>{bg.name}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2 mt-3">
            <button data-testid="fsp-save" onClick={save} disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 bg-[#16C784] text-black hover:bg-[#13b377] disabled:opacity-50 font-rajdhani font-bold uppercase tracking-wider text-sm py-2.5 transition-colors">
              {saving ? <CircleNotchIcon size={15} className="spin-slow" /> : <CheckCircleIcon size={15} weight="fill" />}
              Save
            </button>
            <button data-testid="fsp-generate-now" onClick={generateNow} disabled={generating}
              className="flex items-center justify-center gap-2 border border-[#16C784]/50 text-[#16C784] hover:bg-[#16C784]/10 disabled:opacity-50 font-rajdhani font-bold uppercase tracking-wider text-sm px-4 py-2.5 transition-colors">
              {generating ? <CircleNotchIcon size={15} className="spin-slow" /> : <LightningIcon size={15} weight="fill" />}
              Generate 1 now
            </button>
          </div>
          <p className="font-mono text-[9px] text-[#9c9ca8] mt-3 leading-relaxed">
            FSP picks your next top idea, generates a 9:16 cover image, a Seedance 1.5 Pro video, and a viral title & description,
            then drops it in the queue. When Autopilot is ON and YouTube is connected, each generated Short is
            auto-uploaded to your channel (Private until your app passes YouTube's review) and you get an email
            confirmation. Otherwise, hit Publish to upload manually.
          </p>

          {/* Long-Form Studio */}
          <div data-testid="longform-studio" className="mt-5 border border-[#8B5CF6]/30 bg-[#0C0C0E] p-4"
            style={{ boxShadow: "0 0 12px rgba(139,92,246,0.06)" }}>
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#A78BFA] mb-1">
              <FilmSlateIcon size={14} weight="fill" /> Long-Form Studio
            </div>
            <p className="font-mono text-[9px] text-[#9c9ca8] mb-3 leading-relaxed">
              Fully AI-made 3–5 min 16:9 video: script → AI voiceover → Seedance 1.5 Pro scenes → stitched to one MP4.
              Renders in ~10–25 min &amp; uses your OpenAI + AtlasCloud (Seedance 1.5 Pro) + ElevenLabs credits.
            </p>
            <textarea
              data-testid="longform-topic"
              value={lfTopic}
              onChange={(e) => setLfTopic(e.target.value)}
              rows={2}
              placeholder="Topic, e.g. 'The wildest cheese-pull burgers ever made' or 'How to make crispy Korean corn dogs'"
              className="w-full bg-[#050505] border border-[#222226] focus:border-[#8B5CF6] text-white font-mono text-xs px-3 py-2 outline-none resize-none placeholder:text-[#3a3a40] transition-colors mb-3"
            />
            {voices.length > 0 && (
              <div className="mb-3">
                <label className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#9c9ca8]">Narrator voice</label>
                <div className="flex items-center gap-2 mt-1">
                  <select
                    data-testid="voice-select"
                    value={voiceId}
                    onChange={(e) => setVoiceId(e.target.value)}
                    className="flex-1 bg-[#050505] border border-[#222226] focus:border-[#8B5CF6] text-white font-mono text-xs px-2.5 py-2 outline-none transition-colors"
                  >
                    {voices.map((v) => (
                      <option key={v.voice_id} value={v.voice_id}>{v.name}</option>
                    ))}
                  </select>
                  {voices.find((v) => v.voice_id === voiceId)?.preview_url && (
                    <button
                      type="button"
                      data-testid="voice-preview"
                      onClick={() => new Audio(voices.find((v) => v.voice_id === voiceId).preview_url).play()}
                      className="border border-[#8B5CF6]/40 text-[#A78BFA] hover:bg-[#8B5CF6]/10 px-2.5 py-2 transition-colors"
                      title="Preview voice"
                    >
                      <SpeakerHighIcon size={14} />
                    </button>
                  )}
                </div>
              </div>
            )}
            <button data-testid="longform-generate" onClick={generateLongform} disabled={lfBusy}
              className="w-full flex items-center justify-center gap-2 bg-[#8B5CF6] text-white hover:bg-[#7c4ee0] disabled:opacity-50 font-rajdhani font-bold uppercase tracking-wider text-sm py-2.5 transition-colors">
              {lfBusy ? <CircleNotchIcon size={15} className="spin-slow" /> : <FilmSlateIcon size={15} weight="fill" />}
              Generate Long-Form (~10–25 min)
            </button>
          </div>

          {/* Recipe from YouTube */}
          <div data-testid="recipe-studio" className="mt-4 border border-[#F59E0B]/30 bg-[#0C0C0E] p-4"
            style={{ boxShadow: "0 0 12px rgba(245,158,11,0.06)" }}>
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#FBBF24] mb-1">
              <ForkKnifeIcon size={14} weight="fill" /> Recipe From YouTube
            </div>
            <p className="font-mono text-[9px] text-[#9c9ca8] mb-3 leading-relaxed">
              Enter a meal → pick a source video → we pull its transcript, reword it, build a 3–5 min narrated recipe video, and auto-post it to YouTube.
            </p>
            <div className="flex gap-2 mb-3">
              <input
                data-testid="recipe-topic"
                value={rcTopic}
                onChange={(e) => setRcTopic(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchRecipe()}
                placeholder="Meal, e.g. 'smash burgers'"
                className="flex-1 bg-[#050505] border border-[#222226] focus:border-[#F59E0B] text-white font-mono text-xs px-3 py-2 outline-none placeholder:text-[#3a3a40] transition-colors"
              />
              <button data-testid="recipe-search" onClick={searchRecipe} disabled={rcSearching}
                className="flex items-center gap-1.5 bg-[#F59E0B] text-black hover:bg-[#e08c05] disabled:opacity-50 font-rajdhani font-bold uppercase tracking-wider text-xs px-4 transition-colors">
                {rcSearching ? <CircleNotchIcon size={14} className="spin-slow" /> : <MagnifyingGlassPlusIcon size={14} />} Find
              </button>
            </div>
            {rcCands.length > 0 && (
              <>
                <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#9c9ca8] mb-1.5">Pick a source video</div>
                <div className="space-y-1.5 mb-3 max-h-[260px] overflow-y-auto">
                  {rcCands.map((c) => (
                    <label key={c.video_id} data-testid={`recipe-cand-${c.video_id}`}
                      className={`flex items-center gap-2 p-1.5 border cursor-pointer transition-colors ${rcSel === c.video_id ? "border-[#F59E0B] bg-[#F59E0B]/10" : "border-[#222226] hover:border-[#F59E0B]/40"}`}>
                      <input type="radio" name="recipe-src" value={c.video_id} checked={rcSel === c.video_id}
                        onChange={() => setRcSel(c.video_id)} className="accent-[#F59E0B]" />
                      {c.thumbnail && <img src={c.thumbnail} alt="" referrerPolicy="no-referrer" className="w-16 h-9 object-cover" />}
                      <div className="min-w-0 flex-1">
                        <div className="font-rajdhani font-bold text-xs text-white leading-tight line-clamp-1">{c.title}</div>
                        <div className="font-mono text-[10px] text-[#9c9ca8]">{c.channel_title} · {formatNumber(c.view_count)} views · {Math.round(c.duration_seconds / 60)}m</div>
                      </div>
                    </label>
                  ))}
                </div>
                <button data-testid="recipe-generate" onClick={generateRecipe} disabled={rcBusy || !rcSel}
                  className="w-full flex items-center justify-center gap-2 bg-[#F59E0B] text-black hover:bg-[#e08c05] disabled:opacity-50 font-rajdhani font-bold uppercase tracking-wider text-sm py-2.5 transition-colors">
                  {rcBusy ? <CircleNotchIcon size={15} className="spin-slow" /> : <ForkKnifeIcon size={15} weight="fill" />}
                  Build &amp; Auto-Post Recipe (~10–25 min)
                </button>
              </>
            )}
          </div>
        </div>

        {/* Queue */}
        <div data-testid="fsp-queue">
          {/* YouTube connect bar */}
          {yt && yt.configured && (
            <div data-testid="yt-connect-bar" className="flex items-center justify-between gap-3 bg-[#0C0C0E] border border-[#222226] px-3 py-2.5 mb-3">
              <div className="flex items-center gap-2 min-w-0">
                <YoutubeLogoIcon size={20} className={yt.connected ? "text-[#FF0000]" : "text-[#9c9ca8]"} weight="fill" />
                {yt.connected ? (
                  <span className="font-mono text-[11px] text-[#A0A0AB] truncate">
                    Uploads → <span className="text-[#16C784] font-bold">{yt.yt_channel_title || "connected"}</span>
                  </span>
                ) : (
                  <span className="font-mono text-[11px] text-[#9c9ca8]">Connect YouTube to publish uploads</span>
                )}
              </div>
              {yt.connected ? (
                <button data-testid="yt-disconnect" onClick={disconnectYt}
                  className="font-rajdhani font-bold uppercase tracking-wider text-[10px] px-2.5 py-1 border border-[#222226] text-[#9c9ca8] hover:text-[#FF4D4D] hover:border-[#FF4D4D]/40 transition-colors">
                  Disconnect
                </button>
              ) : (
                <button data-testid="yt-connect" onClick={connectYt} disabled={connecting}
                  className="flex items-center gap-1 bg-[#FF0000] hover:bg-[#d90000] disabled:opacity-60 disabled:cursor-wait text-white font-rajdhani font-bold uppercase tracking-wider text-[10px] px-3 py-1.5 transition-colors">
                  {connecting
                    ? <><CircleNotchIcon size={13} className="spin-slow" /> Opening Google…</>
                    : <><YoutubeLogoIcon size={13} weight="fill" /> Connect YouTube</>}
                </button>
              )}
            </div>
          )}

          {(() => {
          const fspJobs = jobs.filter((j) => j.status === "published" && j.auto_uploaded);
          const publishedJobs = jobs.filter((j) => j.status === "published" && !j.auto_uploaded);
          const laterJobs = jobs.filter((j) => j.status !== "published" && j.bucket === "later");
          const currentJobs = jobs.filter((j) => j.status !== "published" && j.bucket !== "later");
          const tabDefs = [
            { key: "current", label: "Current", icon: StackIcon, color: "#16C784", list: currentJobs },
            { key: "later", label: "Later", icon: BookmarkSimpleIcon, color: "#FFB800", list: laterJobs },
            { key: "published", label: "Published", icon: RocketLaunchIcon, color: "#A3E635", list: publishedJobs },
            { key: "fsp", label: "FSP", icon: RobotIcon, color: "#00E0FF", list: fspJobs },
          ];
          const activeTab = tabDefs.find((t) => t.key === tab) || tabDefs[0];
          const visible = tab === "fsp"
            ? [...activeTab.list].sort((a, b) => new Date(b.published_at || 0) - new Date(a.published_at || 0))
            : activeTab.list;
          const dayKey = (d) => (d ? new Date(d).toDateString() : "unknown");
          const dayLabel = (d) => (d
            ? new Date(d).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })
            : "Date unknown");
          return (
          <>
          <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="fsp-folder-tabs">
            {tabDefs.map((t) => {
              const TIcon = t.icon;
              const on = t.key === tab;
              return (
                <button
                  key={t.key}
                  data-testid={`fsp-tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 border font-rajdhani font-bold uppercase tracking-wider text-[11px] transition-colors ${
                    on ? "bg-white/[0.04]" : "border-[#1a1a1e] text-[#9c9ca8] hover:text-[#A0A0AB]"
                  }`}
                  style={on ? { borderColor: t.color, color: t.color } : undefined}
                >
                  <TIcon size={14} weight={on ? "fill" : "regular"} />
                  {t.label}
                  <span className={`font-mono text-[10px] ${on ? "" : "text-[#3a3a40]"}`}>{t.list.length}</span>
                </button>
              );
            })}
          </div>

          {visible.length > 0 && (() => {
            const allChecked = visible.every((v) => selected.has(v.id));
            return (
              <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="fsp-bulk-bar">
                <button
                  data-testid="fsp-select-all"
                  onClick={() => setSelected(allChecked ? new Set() : new Set(visible.map((v) => v.id)))}
                  className="flex items-center gap-1.5 text-[#A0A0AB] hover:text-[#16C784] font-mono text-[11px] uppercase tracking-wider transition-colors"
                >
                  {allChecked ? <CheckSquareIcon size={16} weight="fill" className="text-[#16C784]" /> : <SquareIcon size={16} />}
                  Select all
                </button>
                {selected.size > 0 && (
                  <>
                    <span className="font-mono text-[11px] text-[#16C784]" data-testid="fsp-bulk-count">{selected.size} selected</span>
                    {tab !== "published" && tab !== "fsp" && (tab === "later" ? (
                      <button data-testid="fsp-bulk-to-current" onClick={() => bulkMove("current")}
                        className="flex items-center gap-1 border border-[#16C784]/40 text-[#16C784] hover:bg-[#16C784]/10 font-rajdhani font-bold uppercase tracking-wider text-[10px] px-2.5 py-1 transition-colors">
                        <StackIcon size={11} /> To Current
                      </button>
                    ) : (
                      <button data-testid="fsp-bulk-later" onClick={() => bulkMove("later")}
                        className="flex items-center gap-1 border border-[#FFB800]/40 text-[#FFB800] hover:bg-[#FFB800]/10 font-rajdhani font-bold uppercase tracking-wider text-[10px] px-2.5 py-1 transition-colors">
                        <BookmarkSimpleIcon size={11} /> Move to Later
                      </button>
                    ))}
                    <button data-testid="fsp-bulk-delete" onClick={bulkDelete}
                      className="flex items-center gap-1 border border-[#FF4D4D]/40 text-[#FF4D4D] hover:bg-[#FF4D4D]/10 font-rajdhani font-bold uppercase tracking-wider text-[10px] px-2.5 py-1 transition-colors">
                      <TrashIcon size={11} /> Delete
                    </button>
                    <button data-testid="fsp-bulk-clear" onClick={clearSel}
                      className="font-mono text-[10px] uppercase tracking-wider text-[#9c9ca8] hover:text-[#A0A0AB] px-1 transition-colors">
                      Clear
                    </button>
                  </>
                )}
              </div>
            );
          })()}

          {visible.length === 0 ? (
            <div className="border border-dashed border-[#222226] py-16 text-center" data-testid={`fsp-empty-${tab}`}>
              <RobotIcon size={30} className="text-[#9c9ca8] mx-auto mb-2" />
              <p className="font-mono text-xs text-[#9c9ca8]">
                {tab === "fsp" ? "Nothing here yet — when autopilot auto-uploads a post to YouTube, it lands here."
                  : tab === "published" ? "Nothing published yet — hit Publish on a Current post."
                  : tab === "later" ? "Nothing saved for Later. Use “Later” on a post to park it here."
                  : "No current posts. Hit “Generate 1 now” or enable autopilot."}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {visible.map((j, idx) => {
                const st = STATUS[j.status] || STATUS.queued;
                const StIcon = st.i;
                const published = j.status === "published";
                // Cache-bust the media URL so the browser refetches when the stored PNG is overwritten
                // (recipe posts store the cover first, then swap in the ❌/✅ thumbnail card mid-build).
                const mediaRev = encodeURIComponent([j.status, j.has_video ? 1 : 0, j.has_image ? 1 : 0, j.steps ? Object.values(j.steps).join("") : ""].join("-"));
                const mediaUrl = `${API}/fsp/media/${j.id}?rev=${mediaRev}`;
                const showDateHeader = tab === "fsp" && (idx === 0 || dayKey(visible[idx - 1]?.published_at) !== dayKey(j.published_at));
                return (
                  <React.Fragment key={j.id}>
                  {showDateHeader && (
                    <div data-testid={`fsp-date-group-${dayKey(j.published_at)}`}
                      className="flex items-center gap-2 pt-2 first:pt-0">
                      <span className="font-mono text-[11px] uppercase tracking-wider text-[#00E0FF]">{dayLabel(j.published_at)}</span>
                      <span className="font-mono text-[10px] text-[#9c9ca8]" data-testid={`fsp-date-count-${dayKey(j.published_at)}`}>
                        {(() => { const n = visible.filter((x) => dayKey(x.published_at) === dayKey(j.published_at)).length; return `${n} post${n === 1 ? "" : "s"}`; })()}
                      </span>
                      <span className="h-px flex-1 bg-[#00E0FF]/20" />
                    </div>
                  )}
                  <div data-testid={`fsp-job-${j.id}`}
                    className={`border p-3 flex gap-3 transition-colors ${
                      selected.has(j.id) ? "ring-1 ring-[#16C784] " : ""
                    }${
                      published
                        ? "bg-[#A3E635]/[0.06] border-[#A3E635]/50 shadow-[0_0_20px_rgba(163,230,53,0.12)]"
                        : "bg-[#0C0C0E] border-[#222226]"
                    }`}>
                    <button
                      data-testid={`fsp-select-${j.id}`}
                      onClick={() => toggleSel(j.id)}
                      className="shrink-0 self-start pt-0.5 text-[#3a3a40] hover:text-[#16C784] transition-colors"
                      title="Select"
                    >
                      {selected.has(j.id)
                        ? <CheckSquareIcon size={20} weight="fill" className="text-[#16C784]" />
                        : <SquareIcon size={20} />}
                    </button>
                    <div
                      className={`${(j.format === "long" || j.aspect === "16:9") ? "w-32 h-20" : "w-20 h-32"} shrink-0 bg-[#050505] border border-[#1a1a1e] overflow-hidden flex items-center justify-center relative group cursor-pointer`}
                      data-testid={`fsp-thumb-${j.id}`}
                      onClick={() => {
                        if (j.has_video) setPreview({ type: "video", src: `${API}/fsp/video/${j.id}`, poster: j.has_image ? mediaUrl : undefined, title: j.title || j.idea_title });
                        else if (j.has_image) setPreview({ type: "image", src: mediaUrl, title: j.title || j.idea_title });
                      }}
                    >
                      {j.has_video ? (
                        <video
                          data-testid={`fsp-video-${j.id}`}
                          src={`${API}/fsp/video/${j.id}`}
                          poster={j.has_image ? mediaUrl : undefined}
                          playsInline
                          muted
                          className="w-full h-full object-cover pointer-events-none"
                        />
                      ) : j.has_image ? (
                        <img src={mediaUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <ImageIcon size={22} className="text-[#3a3a40]" />
                      )}
                      {published && (
                        <div data-testid={`fsp-published-badge-${j.id}`} className="absolute inset-x-0 bottom-0 bg-[#A3E635] text-black flex items-center justify-center gap-1 py-1">
                          <RocketLaunchIcon size={11} weight="fill" />
                          <span className="font-rajdhani font-bold uppercase tracking-wider text-[9px]">Published</span>
                        </div>
                      )}
                      {(j.has_image || j.has_video) && (
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                          <MagnifyingGlassPlusIcon size={20} className="text-[#16C784]" weight="bold" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1" style={{ color: st.c }}>
                        {published ? (
                          <span data-testid={`fsp-status-published-${j.id}`} className="inline-flex items-center gap-1 bg-[#A3E635] text-black font-rajdhani font-bold uppercase tracking-wider text-[10px] px-2 py-0.5">
                            <StIcon size={12} weight="fill" /> Published
                          </span>
                        ) : (
                          <>
                            <StIcon size={13} weight="fill" className={st.spin ? "spin-slow" : ""} />
                            <span className="font-mono text-[10px] uppercase tracking-wider">{st.label}</span>
                          </>
                        )}
                        {published && j.published_at && (
                          <span className="font-mono text-[10px] text-[#A3E635]/70 normal-case tracking-normal">· {timeAgo(j.published_at)}</span>
                        )}
                        {j.source === "auto" && <span className="font-mono text-[9px] text-[#9c9ca8]">· auto</span>}
                        {j.status === "generating" && (
                          <span data-testid={`fsp-timer-${j.id}`} className="inline-flex items-center gap-1 font-mono text-[10px] text-[#FFB800] normal-case tracking-normal">
                            <ClockIcon size={11} /> {fmtElapsed(j.created_at)}
                          </span>
                        )}
                        {j.format === "long" && (
                          <span data-testid={`fsp-longform-badge-${j.id}`} className="inline-flex items-center gap-1 bg-[#8B5CF6]/15 border border-[#8B5CF6]/40 text-[#A78BFA] font-rajdhani font-bold uppercase tracking-wider text-[9px] px-1.5 py-0.5">
                            <FilmSlateIcon size={10} weight="fill" /> Long-Form{j.scenes_count ? ` · ${j.scenes_count} scenes` : ""}
                          </span>
                        )}
                        {j.aspect && j.format !== "long" && (
                          <span data-testid={`fsp-aspect-badge-${j.id}`} className={`inline-flex items-center gap-1 font-rajdhani font-bold uppercase tracking-wider text-[9px] px-1.5 py-0.5 border ${j.aspect === "16:9" ? "bg-[#8B5CF6]/15 border-[#8B5CF6]/40 text-[#A78BFA]" : "bg-[#16C784]/12 border-[#16C784]/35 text-[#16C784]"}`}>
                            {j.aspect === "16:9" ? "16:9 · Long" : "9:16 · Short"}
                          </span>
                        )}
                      </div>
                      <div className="font-rajdhani font-bold text-white text-base leading-tight line-clamp-2">{j.title || j.idea_title || "…"}</div>
                      {j.youtube_url && j.published_at && (
                        <p className="flex items-center gap-1 font-mono text-[10px] text-[#A3E635] mt-1" data-testid={`fsp-sent-at-${j.id}`}>
                          <YoutubeLogoIcon size={11} weight="fill" /> Sent to YouTube {formatDateTime(j.published_at)}
                        </p>
                      )}
                      {j.description && <p className="font-mono text-[10px] text-[#A0A0AB] mt-1 line-clamp-2">{j.description}</p>}
                      {j.spell_corrected && (
                        <p className="flex items-center gap-1 font-mono text-[9px] text-[#16C784] mt-1">
                          <CheckCircleIcon size={11} weight="fill" /> On-image text spell-corrected
                        </p>
                      )}
                      {j.error && <p className="font-mono text-[10px] text-[#FF4D4D] mt-1">{j.error}</p>}
                      {j.format === "long" && j.status === "generating" && (
                        <p className="flex items-center gap-1 font-mono text-[9px] text-[#A78BFA] mt-1">
                          <CircleNotchIcon size={11} className="spin-slow" /> Rendering long-form video (~10–25 min): script → voiceover → scenes → stitch…
                        </p>
                      )}
                      {j.image_error && !j.has_image && j.status === "ready" && (
                        <p className="font-mono text-[9px] text-[#FFB800] mt-1">Image skipped (LLM budget/credits). Copy top up to enable images.</p>
                      )}
                      {j.video_generating && (
                        <p className="flex items-center gap-1 font-mono text-[9px] text-[#FFB800] mt-1">
                          <CircleNotchIcon size={11} className="spin-slow" />
                          {j.video_attempts > 0
                            ? `Auto-retrying video (attempt ${j.video_attempts}/3)…`
                            : "Generating Seedance video…"}
                        </p>
                      )}
                      {!j.video_generating && !j.has_video && j.has_image && j.status === "ready" && (
                        <p className="font-mono text-[9px] text-[#FF4D4D] mt-1" data-testid={`fsp-video-error-${j.id}`}>
                          Video not generated{j.video_error ? `: ${j.video_error}` : " (Seedance step failed)."}
                          {(j.video_attempts || 0) < 3
                            ? " — will auto-retry shortly, or hit “Retry video”."
                            : " — auto-retry stopped after 3 tries; hit “Retry video”."}
                        </p>
                      )}
                      {j.steps?.review === "running" && (
                        <p className="flex items-center gap-1 font-mono text-[9px] text-[#16C784] mt-1" data-testid={`fsp-review-hint-${j.id}`}>
                          <CircleNotchIcon size={11} className="spin-slow" />
                          ChatGPT reviewing post…
                        </p>
                      )}
                      {j.status === "generating" && (
                        <p className="flex items-center gap-1 font-mono text-[9px] text-[#9c9ca8] mt-1" data-testid={`fsp-generating-hint-${j.id}`}>
                          {(Date.now() - new Date(j.created_at).getTime()) / 1000 > 240
                            ? <><WarningIcon size={11} className="text-[#FFB800]" /> This is taking longer than usual — you can cancel and try again or switch service.</>
                            : <>Generating… you can cancel anytime below.</>}
                        </p>
                      )}
                      {j.video_error && (
                        <p className="flex items-start gap-1 font-mono text-[9px] text-[#FFB800] mt-1" data-testid={`fsp-video-note-${j.id}`}>
                          <WarningIcon size={11} className="mt-[1px] shrink-0" /> {j.video_error}
                        </p>
                      )}
                      {j.publishing && (
                        <p className="flex items-center gap-1 font-mono text-[9px] text-[#16C784] mt-1" data-testid={`fsp-publishing-${j.id}`}>
                          <CircleNotchIcon size={11} className="spin-slow" /> Uploading to YouTube… this can take a minute or two, no need to wait here.
                        </p>
                      )}
                      {j.publish_error && !j.publishing && (
                        <p className="flex items-start gap-1 font-mono text-[9px] text-[#FF4D4D] mt-1" data-testid={`fsp-publish-error-${j.id}`}>
                          <WarningIcon size={11} className="mt-[1px] shrink-0" /> {j.publish_error}
                        </p>
                      )}
                      {j.thumbnail_error && !j.publishing && (
                        <p className="flex items-start gap-1 font-mono text-[9px] text-[#FFB800] mt-1" data-testid={`fsp-thumb-error-${j.id}`}>
                          <WarningIcon size={11} className="mt-[1px] shrink-0" /> Posted, but custom thumbnail was rejected by YouTube: {j.thumbnail_error}
                        </p>
                      )}
                      {j.steps && j.status !== "published" && j.format !== "long" && <StepStrip steps={j.steps} />}
                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        {j.status === "generating" && (
                          <button data-testid={`fsp-cancel-${j.id}`} onClick={() => cancelJob(j.id)}
                            className="flex items-center gap-1 border border-[#FF4D4D]/40 text-[#FF4D4D] hover:bg-[#FF4D4D]/10 font-rajdhani font-bold uppercase tracking-wider text-[10px] px-2.5 py-1 transition-colors">
                            <XIcon size={12} weight="bold" /> Cancel
                          </button>
                        )}
                        {j.status === "ready" && (
                          <button data-testid={`fsp-publish-${j.id}`} onClick={() => publish(j.id)} disabled={j.publishing}
                            className="flex items-center gap-1 bg-[#16C784] text-black hover:bg-[#13b377] disabled:opacity-50 disabled:cursor-not-allowed font-rajdhani font-bold uppercase tracking-wider text-[10px] px-2.5 py-1 transition-colors">
                            {j.publishing
                              ? <><CircleNotchIcon size={12} className="spin-slow" /> Publishing…</>
                              : <><RocketLaunchIcon size={12} weight="fill" /> Publish to YouTube</>}
                          </button>
                        )}
                        {j.youtube_url && (() => {
                          const scheduled = j.youtube_privacy === "scheduled";
                          const label = scheduled ? "Scheduled" : (j.youtube_privacy === "public" ? "Public" : "Private");
                          const cls = scheduled
                            ? "border-[#A3E635]/40 text-[#A3E635] hover:bg-[#A3E635]/10"
                            : "border-[#FF0000]/40 text-[#FF4D4D] hover:bg-[#FF0000]/10";
                          return (
                            <a data-testid={`fsp-yt-link-${j.id}`} href={j.youtube_url} target="_blank" rel="noreferrer"
                              className={`flex items-center gap-1 border font-rajdhani font-bold uppercase tracking-wider text-[10px] px-2.5 py-1 transition-colors ${cls}`}>
                              <YoutubeLogoIcon size={12} weight="fill" /> On YouTube · {label}
                            </a>
                          );
                        })()}
                        {j.has_video && (
                          <a data-testid={`fsp-dl-video-${j.id}`} href={`${API}/fsp/video/${j.id}`} download target="_blank" rel="noreferrer"
                            className="flex items-center gap-1 border border-[#222226] text-[#A0A0AB] hover:text-[#16C784] font-rajdhani uppercase tracking-wider text-[10px] px-2.5 py-1 transition-colors">
                            <FilmSlateIcon size={11} /> Video
                          </a>
                        )}
                        {j.has_image && (
                          <a data-testid={`fsp-dl-thumb-${j.id}`} href={mediaUrl} download={`${(j.title || j.idea_title || "thumbnail").replace(/[^a-z0-9]+/gi, "-").slice(0, 40)}.png`} target="_blank" rel="noreferrer"
                            className="flex items-center gap-1 border border-[#222226] text-[#A0A0AB] hover:text-[#16C784] font-rajdhani uppercase tracking-wider text-[10px] px-2.5 py-1 transition-colors">
                            <ImageIcon size={11} /> Thumbnail
                          </a>
                        )}
                        {!j.has_video && j.has_image && j.status === "ready" && (
                          <button data-testid={`fsp-retry-video-${j.id}`} onClick={() => retryVideo(j.id)} disabled={j.video_generating}
                            className="flex items-center gap-1 border border-[#FFB800]/40 text-[#FFB800] hover:bg-[#FFB800]/10 disabled:opacity-40 font-rajdhani uppercase tracking-wider text-[10px] px-2.5 py-1 transition-colors">
                            {j.video_generating ? <CircleNotchIcon size={11} className="spin-slow" /> : <FilmSlateIcon size={11} />} Retry video
                          </button>
                        )}
                        {j.title && (
                          <button onClick={() => copyText(j.title, "Title")}
                            className="flex items-center gap-1 border border-[#222226] text-[#A0A0AB] hover:text-[#16C784] font-rajdhani uppercase tracking-wider text-[10px] px-2.5 py-1 transition-colors">
                            <CopyIcon size={11} /> Title
                          </button>
                        )}
                        {j.description && (
                          <button onClick={() => copyText(j.description, "Description")}
                            className="flex items-center gap-1 border border-[#222226] text-[#A0A0AB] hover:text-[#16C784] font-rajdhani uppercase tracking-wider text-[10px] px-2.5 py-1 transition-colors">
                            <CopyIcon size={11} /> Desc
                          </button>
                        )}
                        <button data-testid={`fsp-regenerate-${j.id}`} onClick={() => regenerate(j.id)}
                          className="flex items-center gap-1 border border-[#222226] text-[#A0A0AB] hover:text-[#16C784] hover:border-[#16C784]/40 font-rajdhani uppercase tracking-wider text-[10px] px-2.5 py-1 transition-colors">
                          <ArrowsClockwiseIcon size={11} /> Regenerate
                        </button>
                        {j.status !== "published" && (
                          tab === "later" ? (
                            <button data-testid={`fsp-move-current-${j.id}`} onClick={() => setBucket(j.id, "current")}
                              className="flex items-center gap-1 border border-[#16C784]/40 text-[#16C784] hover:bg-[#16C784]/10 font-rajdhani uppercase tracking-wider text-[10px] px-2.5 py-1 transition-colors">
                              <StackIcon size={11} /> To Current
                            </button>
                          ) : (
                            <button data-testid={`fsp-move-later-${j.id}`} onClick={() => setBucket(j.id, "later")}
                              className="flex items-center gap-1 border border-[#FFB800]/40 text-[#FFB800] hover:bg-[#FFB800]/10 font-rajdhani uppercase tracking-wider text-[10px] px-2.5 py-1 transition-colors">
                              <BookmarkSimpleIcon size={11} /> Later
                            </button>
                          )
                        )}
                        {j.status !== "published" && (
                          <button data-testid={`fsp-mark-published-${j.id}`} onClick={() => markPublished(j.id)}
                            className="flex items-center gap-1 border border-[#A3E635]/40 text-[#A3E635] hover:bg-[#A3E635]/10 font-rajdhani uppercase tracking-wider text-[10px] px-2.5 py-1 transition-colors">
                            <RocketLaunchIcon size={11} /> Published
                          </button>
                        )}
                        {j.status === "published" && (
                          <button data-testid={`fsp-move-current-${j.id}`} onClick={() => unpublish(j.id)}
                            className="flex items-center gap-1 border border-[#16C784]/40 text-[#16C784] hover:bg-[#16C784]/10 font-rajdhani uppercase tracking-wider text-[10px] px-2.5 py-1 transition-colors">
                            <StackIcon size={11} /> Move to Current
                          </button>
                        )}
                        <button data-testid={`fsp-delete-${j.id}`} onClick={() => del(j.id)}
                          className="flex items-center gap-1 border border-[#222226] text-[#9c9ca8] hover:text-[#FF4D4D] hover:border-[#FF4D4D]/40 font-rajdhani uppercase tracking-wider text-[10px] px-2.5 py-1 transition-colors ml-auto">
                          <TrashIcon size={11} />
                        </button>
                      </div>
                    </div>
                  </div>
                  </React.Fragment>
                );
              })}
            </div>
          )}
          </>
          );
          })()}
        </div>
      </div>

      {/* Lightbox preview */}
      {preview && (
        <div
          data-testid="fsp-lightbox"
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
        >
          <button
            data-testid="fsp-lightbox-close"
            onClick={() => setPreview(null)}
            className="absolute top-5 right-5 w-10 h-10 border border-[#222226] text-white hover:text-[#16C784] hover:border-[#16C784]/50 flex items-center justify-center transition-colors"
          >
            <XIcon size={20} weight="bold" />
          </button>
          <div className="flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <div className="h-[78vh] aspect-[9/16] bg-[#050505] border border-[#222226] overflow-hidden flex items-center justify-center">
              {preview.type === "video" ? (
                <video
                  data-testid="fsp-lightbox-video"
                  src={preview.src}
                  poster={preview.poster}
                  controls
                  autoPlay
                  playsInline
                  className="w-full h-full object-contain"
                />
              ) : (
                <img data-testid="fsp-lightbox-image" src={preview.src} alt="" className="w-full h-full object-contain" />
              )}
            </div>
            {preview.title && (
              <p className="font-rajdhani font-bold text-white text-lg text-center max-w-[80vw] line-clamp-2">{preview.title}</p>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default FSPPanel;
